import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js'
import { useGLTFScene } from './CharacterModel'
import { useNarration } from '../../audio/NarrationContext'

/**
 * Ready Player Me avatar (67 ARKit morph targets) — the companion's own
 * private instance, framed as a face-and-shoulder portrait. Mouth is driven
 * by the narration analyser (viseme cycling + jaw openness), with blink and
 * subtle head sway. Suspense-free loading with retries; onMissing lets the
 * porthole show a fallback instead of an empty circle.
 */
export default function SpeakingAvatar({ speaking = false, onMissing, ...props }) {
  const group = useRef()
  const blink = useRef({ next: 2, closing: 0 })
  const viseme = useRef({ name: 'viseme_PP', switchAt: 0, openness: 0 })
  const gltf = useGLTFScene('/models/speaking_avatar_readyplayerme.glb', { onMissing })
  const { analyser, data } = useNarration()

  const scene = useMemo(() => (gltf ? skeletonClone(gltf.scene) : null), [gltf])

  useEffect(() => {
    if (!scene) return
    scene.traverse((child) => {
      if (child.isMesh) child.frustumCulled = false
    })
  }, [scene])

  const armBones = useRef(null)
  const findArmBones = () => {
    if (armBones.current) return armBones.current
    if (!scene) return null
    const found = {}
    scene.traverse((child) => {
      if (!child.isBone) return
      const n = child.name.toLowerCase()
      if (n === 'leftarm') found.left = { bone: child, bind: child.quaternion.clone() }
      if (n === 'rightarm') found.right = { bone: child, bind: child.quaternion.clone() }
    })
    if (found.left && found.right) armBones.current = found
    return armBones.current
  }

  const Z_AXIS = { x: 0, y: 0, z: 1 }

  // settle the T-pose into a natural at-rest stance.
  // world-axis rotation — bone-local axes are rotated by the bind pose,
  // so local rotation.z lands on the wrong axis entirely.
  const restPose = () => {
    const bones = findArmBones()
    if (!bones) return
    bones.left.bone.quaternion.copy(bones.left.bind)
    bones.left.bone.rotateOnWorldAxis(Z_AXIS, -1.28)
    bones.right.bone.quaternion.copy(bones.right.bind)
    bones.right.bone.rotateOnWorldAxis(Z_AXIS, 1.28)
  }

  const morphMeshes = useMemo(() => {
    if (!scene) return []
    const meshes = []
    scene.traverse((child) => {
      if (child.isMesh && child.morphTargetDictionary) meshes.push(child)
    })
    return meshes
  }, [scene])

  const influences = useRef({})

  const setMorph = (name, value) => {
    influences.current[name] = value
    for (const mesh of morphMeshes) {
      const idx = mesh.morphTargetDictionary[name]
      if (idx !== undefined && mesh.morphTargetInfluences) {
        mesh.morphTargetInfluences[idx] = value
      }
    }
  }

  useFrame((state) => {
    if (!group.current) return
    const t = state.clock.elapsedTime

    restPose()

    // ---- voice energy -> mouth ----
    let openness = 0
    let low = 0
    let mid = 0
    let high = 0
    const analyserNode = analyser.current
    if (analyserNode) {
      analyserNode.getByteFrequencyData(data.current)
      const arr = data.current
      const avg = (a, b) => {
        let s = 0
        for (let i = a; i < b; i++) s += arr[i]
        return s / Math.max(1, b - a) / 255
      }
      low = avg(0, 3)
      mid = avg(3, 9)
      high = avg(9, 24)
      openness = Math.min(1, (mid * 1.5 + low * 0.7) * 1.45)
    } else if (speaking) {
      // synthetic fallback — moderate speech rhythm (only while speaking)
      openness = (Math.sin(t * 6.5) * 0.5 + 0.5) * (Math.sin(t * 1.9) > -0.3 ? 0.45 : 0.04)
      low = Math.sin(t * 4.2) * 0.5 + 0.5
      mid = Math.sin(t * 5.6) * 0.5 + 0.5
      high = Math.sin(t * 8.1) * 0.5 + 0.5
    }

    const v = viseme.current
    if (t > v.switchAt) {
      // moderate pace — visemes change at a natural syllable rate (~140-260ms)
      v.switchAt = t + 0.14 + Math.random() * 0.12
      if (openness < 0.06) v.name = 'viseme_PP'
      else if (high > mid * 0.85) v.name = Math.random() > 0.5 ? 'viseme_E' : 'viseme_I'
      else if (low > mid) v.name = Math.random() > 0.5 ? 'viseme_O' : 'viseme_aa'
      else v.name = Math.random() > 0.35 ? 'viseme_aa' : 'viseme_O'
    }
    v.openness += (openness - v.openness) * 0.14

    // decay all visemes, then raise the active one
    for (const name of [
      'viseme_PP',
      'viseme_aa',
      'viseme_E',
      'viseme_I',
      'viseme_O',
      'viseme_U',
      'mouthOpen',
    ]) {
      setMorph(name, Math.max(0, (influences.current[name] || 0) - 0.13))
    }
    setMorph(v.name, v.openness * 0.78)
    setMorph('mouthOpen', v.openness * 0.42)

    // ---- blink ----
    const b = blink.current
    if (t > b.next) {
      b.closing = 0.14
      b.next = t + 2.2 + Math.random() * 3
    }
    if (b.closing > 0) {
      b.closing -= 1 / 60
      setMorph('eyeBlinkLeft', 1)
      setMorph('eyeBlinkRight', 1)
    } else {
      setMorph('eyeBlinkLeft', 0)
      setMorph('eyeBlinkRight', 0)
    }

    // ---- alive: gentle sway ----
    group.current.rotation.y = Math.sin(t * 0.7) * 0.07
    group.current.rotation.z = Math.sin(t * 0.9) * 0.015
    group.current.position.y =
      (props.position ? props.position[1] : 0) + Math.sin(t * 1.3) * 0.008
  })

  return (
    <group ref={group} {...props}>
      {scene && <primitive object={scene} />}
    </group>
  )
}
